import { Suspense, lazy } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { AdminLayout } from './layouts/AdminLayout';
import { DashboardPage } from './pages/DashboardPage';
import { TenantsListPage } from './pages/TenantsListPage';
import { BuiltInUsersPage } from './pages/BuiltInUsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { ItilConfigPage } from './pages/ItilConfigPage';
import { LogsPage } from './pages/monitoring/LogsPage';
import { JobsPage } from './pages/monitoring/JobsPage';
import { ServiceBusPage } from './pages/monitoring/ServiceBusPage';
import { EventsManagementPage } from './pages/monitoring/EventsManagementPage';
import { PerformancePage } from './pages/monitoring/PerformancePage';
import { AutoClosePage } from './pages/monitoring/AutoClosePage';
import { AssistantUsagePage } from './pages/monitoring/AssistantUsagePage';
import { SystemInfoPage } from './pages/SystemInfoPage';
import { IntegrationsTeamsPage } from './pages/IntegrationsTeamsPage';
import { DocumentationPage } from './pages/DocumentationPage';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { LoginPage } from './pages/LoginPage';
import { queryClient } from './lib/queryClient';

const EmailHistoryPage = lazy(async () => {
  const module = await import('./pages/monitoring/EmailHistoryPage');
  return { default: module.EmailHistoryPage };
});

const SignInLogsPage = lazy(async () => {
  const module = await import('./pages/monitoring/SignInLogsPage');
  return { default: module.SignInLogsPage };
});

const FeedbackBugsPage = lazy(async () => {
  const module = await import('./pages/monitoring/FeedbackBugsPage');
  return { default: module.FeedbackBugsPage };
});

const TableBrowserPage = lazy(() => import('./pages/tools/TableBrowserPage').then((m) => ({ default: m.TableBrowserPage })));

function SystemAdminGuard() {
  const { isLoading, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" showLabel label="Loading Admin Portal..." />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show access denied if authenticated but not SystemAdmin
  if (user?.role !== 'SystemAdmin') {
    return <AccessDeniedPage />;
  }

  return <AdminLayout />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LocaleProvider>
          <ToastProvider>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                  <Routes>
                    <Route element={<SystemAdminGuard />}>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/tenants" element={<TenantsListPage />} />
                      <Route path="/system-users" element={<BuiltInUsersPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/itil-configuration" element={<ItilConfigPage />} />
                      <Route path="/integrations/teams" element={<IntegrationsTeamsPage />} />
                      <Route path="/monitoring/logs" element={<LogsPage />} />
                      <Route path="/monitoring/jobs" element={<JobsPage />} />
                      <Route path="/monitoring/service-bus" element={<ServiceBusPage />} />
                      <Route path="/monitoring/events" element={<EventsManagementPage />} />
                      <Route path="/monitoring/performance" element={<PerformancePage />} />
                      <Route path="/monitoring/auto-close" element={<AutoClosePage />} />
                      <Route
                        path="/monitoring/email-history"
                        element={(
                          <Suspense fallback={<LoadingSpinner size="md" showLabel label="Loading Email History..." />}>
                            <EmailHistoryPage />
                          </Suspense>
                        )}
                      />
                      <Route path="/monitoring/assistant-usage" element={<AssistantUsagePage />} />
                      <Route
                        path="/monitoring/signin-logs"
                        element={(
                          <Suspense fallback={<LoadingSpinner size="md" showLabel label="Loading Sign-In Logs..." />}>
                            <SignInLogsPage />
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/tools/feedback-bugs"
                        element={(
                          <Suspense fallback={<LoadingSpinner size="md" showLabel label="Loading Feedback & Bugs..." />}>
                            <FeedbackBugsPage />
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/tools/table-browser"
                        element={(
                          <Suspense fallback={<LoadingSpinner size="md" showLabel label="Loading Table Browser..." />}>
                            <TableBrowserPage />
                          </Suspense>
                        )}
                      />
                      <Route path="/security/signin-logs" element={<Navigate to="/monitoring/signin-logs" replace />} />
                      <Route path="/feedback-bugs" element={<Navigate to="/tools/feedback-bugs" replace />} />
                      <Route path="/system-info" element={<SystemInfoPage />} />
                      <Route path="/documentation/*" element={<DocumentationPage />} />
                    </Route>
                    <Route path="/access-denied" element={<AccessDeniedPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </BrowserRouter>
              </QueryClientProvider>
            </AuthProvider>
          </ToastProvider>
        </LocaleProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
